import {
  MAX_REPORT_ATTACHMENTS,
  REPORT_ATTACHMENT_TTL_SEC,
  REPORT_ATTACHMENT_MIME_TYPES,
  REPORT_REASONS,
  reportRequiresAttachment,
  type ReportReason,
  type SubmitReportInput,
} from "@bing/shared";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { fa } from "../i18n/fa";
import {
  prepareReportAttachment,
  ReportAttachmentError,
  reportAttachmentRejection,
  type ReportAttachmentErrorCode,
} from "./report-attachment";
import { Sheet } from "./social-ui";

/**
 * «گزارش کاربر» bottom sheet — pick a reason, attach evidence, submit.
 *
 * ⚠️ EVIDENCE IS CONDITIONALLY MANDATORY, AND THE GATE IS THE UPLOAD, NOT THE PICK. For a reason
 * where one frame proves something (توهین / محتوای نامناسب) Submit stays disabled until at least one
 * file is ON THE SERVER. تقلب and هرزنامه are behavioural patterns a screenshot cannot capture, so
 * they may be filed without one — demanding evidence there would block legitimate reports.
 *
 * ⚠️ SUBMIT IS BLOCKED WHILE ANY FILE IS STILL WORKING, WHATEVER THE REASON. Otherwise a spam report
 * could be filed a second before its file lands, arriving WITHOUT the evidence the user believes
 * they attached — the confusing failure, not the safe one.
 *
 * ⚠️ THE PROGRESS BAR APPEARS THE INSTANT A PICK IS ACCEPTED, not when the upload starts. Preparing
 * (the PNG→JPEG re-encode) is the slow part on a big screenshot, so a bar that waited for the upload
 * looked broken for the first second or two. `working` covers both stages; only its LABEL changes.
 *
 * ⚠️ …BUT A PICK WE ARE GOING TO REFUSE NEVER GETS A BAR AT ALL. Type and size are decided
 * synchronously, before any chip is created (see `reportAttachmentRejection`). Rendering a working
 * chip first and validating afterwards showed a progress bar for a file that was never going
 * anywhere, which reads as «it optimised and uploaded it, then told me it was too big».
 *
 * ⚠️ NO PREVIEW IS EVER DECODED. The re-encode already pays one ~10 MB bitmap decode per pick; a
 * second one just to draw a thumbnail is the memory pressure that used to kill the avatar crop sheet
 * on LOW-end Android. Files show as chips.
 */

const ACCEPT = REPORT_ATTACHMENT_MIME_TYPES.join(",");

const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const faNum = (n: number | string): string =>
  String(n).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);

function faBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${faNum((bytes / (1024 * 1024)).toFixed(1))} مگابایت`;
  return `${faNum(Math.max(1, Math.round(bytes / 1024)))} کیلوبایت`;
}

/**
 * ⚠️ BUILT FROM PRIMITIVES (rect / circle / 2–3-point paths), NOT HAND-WRITTEN PATH DATA.
 *
 * The first attempt at these was long `d` strings written from memory and they rendered as garbage
 * on a real device. A `<rect>` or a `<circle>` cannot render as garbage: the geometry is declared,
 * not drawn. Every glyph below is verifiable by its bounding box, and they match the app's existing
 * idiom — self-contained `currentColor` SVGs at 24×24, like the lobby drawer's.
 */
const REASON_GLYPH: Record<ReportReason, ReactNode> = {
  // هرزنامه — a stack of messages
  SPAM: (
    <>
      <rect x="3" y="5" width="18" height="13" rx="3" />
      <path d="M7 10h10" />
      <path d="M7 13.5h6" />
    </>
  ),
  // توهین یا آزار — a speech bubble carrying an exclamation
  ABUSE: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="3" />
      <path d="M8 16l-2 4 5.5-4" />
      <path d="M12 7.5v3" />
      <circle cx="12" cy="12.7" r="0.9" fill="currentColor" stroke="none" />
    </>
  ),
  // تقلب — two playing cards, which is what cheating means on this platform
  CHEATING: (
    <>
      <rect x="3.5" y="5" width="9" height="13" rx="2" transform="rotate(-11 8 11.5)" />
      <rect x="11.5" y="5" width="9" height="13" rx="2" transform="rotate(11 16 11.5)" />
    </>
  ),
  // محتوای نامناسب — a warning triangle
  INAPPROPRIATE: (
    <>
      <path d="M12 4L21 19H3Z" />
      <path d="M12 10v3.5" />
      <circle cx="12" cy="16.2" r="0.9" fill="currentColor" stroke="none" />
    </>
  ),
  // سایر
  OTHER: (
    <>
      <circle cx="5.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
};

/** «بازگشت» — an arrow pointing at the RTL start, where this app's way back is. */
const BACK_GLYPH: ReactNode = (
  <>
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </>
);

/** The evidence section's own glyph — a photo frame. */
const ATTACH_GLYPH: ReactNode = (
  <>
    <rect x="3" y="5" width="18" height="14" rx="3" />
    <circle cx="8.5" cy="10" r="1.6" />
    <path d="M4.5 17l4.5-4.5 3.5 3.5 3-2.5 4 3.5" />
  </>
);

function Glyph({ children, size = 19 }: { children: ReactNode; size?: number }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function ReasonIcon({ reason, active }: { reason: ReportReason; active: boolean }): ReactNode {
  return (
    <span
      aria-hidden="true"
      className={`flex h-9 w-9 flex-none items-center justify-center rounded-full transition-colors ${
        active
          ? "bg-secondary/25 text-secondary"
          : "bg-surface-container-highest text-on-surface-variant"
      }`}
    >
      <Glyph>{REASON_GLYPH[reason]}</Glyph>
    </span>
  );
}

/** ⚠️ The red star is the ONLY marker of a required field — keep it next to the label it governs. */
function RequiredStar(): ReactNode {
  return (
    <span aria-hidden="true" className="font-body-md text-body-md text-error">
      *
    </span>
  );
}

type AttachItem =
  | { key: string; phase: "working"; name: string; ratio: number; label: string }
  | { key: string; phase: "ready"; name: string; id: string; bytes: number; at: number }
  | { key: string; phase: "failed"; name: string; message: string };

let keySeq = 0;

/**
 * ⚠️ A DRAFT CAN OUTLIVE ITS EVIDENCE. The server holds an upload for `REPORT_ATTACHMENT_TTL_SEC`
 * and refuses an expired handle; a table keeps this sheet for the whole match. So a chip older
 * than the hold (less a margin — `at` is when the RESPONSE landed, after the server's clock began)
 * is marked for a re-pick instead of reading «ready» over a submit that can only fail.
 */
const ATTACHMENT_STALE_MS = (REPORT_ATTACHMENT_TTL_SEC - 300) * 1000;

/**
 * What only a LIVE TABLE can offer. Absent everywhere else (the profile screen), so nothing below
 * may promise a table that is not there.
 */
export interface ReportSheetTable {
  /** It is OUR turn on the table right now — a timed turn plays itself, and on a public table
   *  five of them in a row remove the seat, so the sheet must say so and offer the felt. */
  myTurn: boolean;
  /** The screenshot door lands on the table's CHAT (it has one) rather than the felt — say so. */
  shotLandsOnChat: boolean;
  /** Hide this sheet and show the table where the evidence is. The draft is kept. */
  onTakeScreenshot: () => void;
  /** Hide this sheet AND whatever it was opened over — back to the felt. The draft is kept. */
  onBackToTable: () => void;
}

export function ReportSheet({
  name,
  onSubmit,
  onUpload,
  onClose,
  hidden = false,
  table,
  onDraftChange,
}: {
  name: string;
  onSubmit: (input: SubmitReportInput) => Promise<void>;
  onUpload: (
    blob: Blob,
    mime: string,
    onProgress: (ratio: number) => void,
  ) => Promise<{ attachmentId: string }>;
  onClose: () => void;
  /**
   * Off-screen but ALIVE: the reason, the text, the files — and any upload still in flight — are
   * kept for the return. A table keeps its sheet mounted this way so a player can go and take the
   * screenshot without starting the report over.
   */
  hidden?: boolean;
  table?: ReportSheetTable;
  /** Whether anything has been chosen yet — the table keeps a «continue» row only for a real draft. */
  onDraftChange?: (hasDraft: boolean) => void;
}): ReactNode {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<AttachItem[]>([]);
  /** Refusals that belong to the PICK itself (too many at once), not to any one file. */
  const [attachNotice, setAttachNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const patch = (key: string, next: AttachItem): void =>
    setItems((list) => list.map((i) => (i.key === key ? next : i)));

  /** Files actually occupying one of the three places — a failed chip is a message, not a file. */
  const liveCount = items.filter((i) => i.phase !== "failed").length;

  const refusal = (code: ReportAttachmentErrorCode | null): string =>
    code === "too_large"
      ? fa.report.attachTooLarge
      : code === "unsupported"
        ? fa.report.attachUnsupported
        : fa.report.attachFailed;

  async function ingest(file: File): Promise<void> {
    const key = `a${(keySeq += 1)}`;
    // ⚠️ REFUSE BEFORE THE CHIP EXISTS. This used to push a «در حال آماده‌سازی…» chip WITH ITS
    // PROGRESS BAR and only then await validation, so an over-size pick flashed a bar before saying
    // it was too big — reported on device as «it optimises and uploads it, THEN tells me». The size
    // and type were always checked first; what was wrong was the order of the FEEDBACK.
    const rejection = reportAttachmentRejection(file);
    if (rejection) {
      setItems((list) => [
        ...list,
        { key, phase: "failed", name: file.name, message: refusal(rejection) },
      ]);
      return;
    }
    // Past the cheap checks there IS real work to wait for, so the bar exists from this moment —
    // preparing (the PNG→JPEG re-encode) is part of the wait, not a silent prelude.
    setItems((list) => [
      ...list,
      { key, phase: "working", name: file.name, ratio: 0, label: fa.report.attachPreparing },
    ]);
    let prepared;
    try {
      prepared = await prepareReportAttachment(file);
    } catch (err) {
      patch(key, {
        key,
        phase: "failed",
        name: file.name,
        message: refusal(err instanceof ReportAttachmentError ? err.code : null),
      });
      return;
    }
    patch(key, {
      key,
      phase: "working",
      name: prepared.name,
      ratio: 0,
      label: fa.report.attachUploading,
    });
    try {
      const { attachmentId } = await onUpload(prepared.blob, prepared.mime, (ratio) =>
        setItems((list) =>
          list.map((i) => (i.key === key && i.phase === "working" ? { ...i, ratio } : i)),
        ),
      );
      patch(key, {
        key,
        phase: "ready",
        name: prepared.name,
        id: attachmentId,
        bytes: prepared.blob.size,
        at: Date.now(),
      });
    } catch {
      patch(key, { key, phase: "failed", name: prepared.name, message: fa.report.attachFailed });
    }
  }

  function pickFiles(files: File[]): void {
    setError(null);
    setAttachNotice(null);
    // ⚠️ A FAILED CHIP MUST NOT HOLD A SLOT. Counting it did: two good files plus one failure
    // disabled «افزودن فایل دیگر», and on a reason that REQUIRES evidence three failures left the
    // reporter unable to add a file AND unable to submit — a dead end behind a ✕ they could barely
    // see. Only files that are on their way, or already up, occupy the three places.
    const room = Math.max(0, MAX_REPORT_ATTACHMENTS - liveCount);
    // Say so IMMEDIATELY, before a single re-encode or upload starts. Taking the ones that fit beats
    // discarding the whole pick, so the message names how many were left out.
    if (files.length > room) setAttachNotice(fa.report.attachTooMany(faNum(files.length - room)));
    for (const file of files.slice(0, room)) void ingest(file);
  }

  const ready = items.filter(
    (i): i is Extract<AttachItem, { phase: "ready" }> => i.phase === "ready",
  );
  const working = items.some((i) => i.phase === "working");
  const needsAttachment = reason ? reportRequiresAttachment(reason) : false;
  const full = liveCount >= MAX_REPORT_ATTACHMENTS;
  const blocked = !reason || busy || working || (needsAttachment && ready.length === 0);

  // Told through a ref, keyed on the BOOLEAN: the owner passes a fresh arrow every render, and an
  // effect keyed on it would report on every render rather than on every change.
  const filled = reason !== null || details.trim() !== "" || liveCount > 0;
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  useEffect(() => onDraftChangeRef.current?.(filled), [filled]);

  /** Marks every aged-out chip for a re-pick; true when any did. Read off the CURRENT list. */
  function expireStale(): boolean {
    const now = Date.now();
    const stale = items.some((i) => i.phase === "ready" && now - i.at > ATTACHMENT_STALE_MS);
    if (stale)
      setItems((list) =>
        list.map((i) =>
          i.phase === "ready" && now - i.at > ATTACHMENT_STALE_MS
            ? { key: i.key, phase: "failed", name: i.name, message: fa.report.attachExpired }
            : i,
        ),
      );
    return stale;
  }
  // Coming back to a parked draft is when the player would otherwise meet a dead chip.
  const expireRef = useRef(expireStale);
  expireRef.current = expireStale;
  useEffect(() => {
    if (!hidden) expireRef.current();
  }, [hidden]);

  async function submit(): Promise<void> {
    if (blocked || !reason) return;
    if (expireStale()) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        reason,
        // Kept even when the reason does not demand it — evidence is welcome everywhere.
        ...(ready.length ? { attachmentIds: ready.map((i) => i.id) } : {}),
        ...(details.trim() ? { details: details.trim() } : {}),
      });
      setDone(true);
    } catch {
      setError(fa.report.error);
    } finally {
      setBusy(false);
    }
  }

  // Derived from the shared list, so the sentence cannot name a reason the gate does not enforce.
  const shotReasons = REPORT_REASONS.filter(reportRequiresAttachment)
    .map((r) => `«${fa.report.reasons[r]}»`)
    .join(" و ");

  // AFTER every hook: hidden is a render decision, never a different component.
  if (hidden) return null;

  return (
    <Sheet
      label={fa.report.title(name)}
      onClose={onClose}
      header={
        <div className="flex flex-col gap-2 pb-3">
          <div className="flex items-center gap-2">
            {/* ⚠️ ALWAYS LIVE — nothing chosen, a file uploading, the report submitting. This sheet
                had no visible way out at all (only the backdrop), and a player who could not find
                one left the table instead (2026-10-03). */}
            <button
              type="button"
              onClick={onClose}
              className="flex flex-none cursor-pointer appearance-none items-center gap-1.5 rounded-full border border-solid border-outline bg-surface-container-highest px-3 py-1.5 font-body-md text-body-md font-bold text-on-surface transition-transform active:scale-95"
            >
              <Glyph size={16}>{BACK_GLYPH}</Glyph>
              {fa.report.back}
            </button>
            <p className="m-0 min-w-0 flex-1 break-words font-body-lg text-body-lg text-on-surface">
              {fa.report.title(name)}
            </p>
          </div>
          {table?.myTurn ? (
            <div
              data-testid="report-your-turn"
              role="status"
              className="flex items-center gap-2 rounded-xl bg-secondary/25 px-3 py-2"
            >
              <p className="m-0 min-w-0 flex-1 font-body-md text-caption text-on-surface">
                {fa.report.yourTurn}
              </p>
              <button
                type="button"
                onClick={table.onBackToTable}
                className="flex-none cursor-pointer appearance-none rounded-full border-0 bg-gold-deep px-3 py-1.5 font-body-md text-caption font-bold text-ink-on-gold transition-transform active:scale-95"
              >
                {fa.report.yourTurnAction}
              </button>
            </div>
          ) : null}
        </div>
      }
    >
      {done ? (
        <div className="flex flex-col items-center gap-2 py-lg text-center">
          <span aria-hidden="true" className="text-4xl">
            ✅
          </span>
          <p className="m-0 font-body-md text-body-md text-on-surface">{fa.report.done}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* SAID UP FRONT — before the reasons. It used to surface only after توهین was picked,
              which is how a player learned they needed a screenshot of a table they then could not
              get back to. */}
          <div
            data-testid="report-shot-notice"
            className="flex flex-col gap-2 rounded-2xl bg-secondary/10 px-3.5 py-3"
          >
            <p className="m-0 flex items-start gap-2 font-body-md text-body-md text-on-surface">
              <span aria-hidden="true" className="flex-none text-secondary">
                <Glyph size={18}>{ATTACH_GLYPH}</Glyph>
              </span>
              <span>{fa.report.shotNotice(shotReasons)}</span>
            </p>
            {table ? (
              <>
                <p className="m-0 font-body-md text-caption text-on-surface-variant">
                  {fa.report.shotNoticeTable}
                </p>
                <button
                  type="button"
                  onClick={table.onTakeScreenshot}
                  className="cursor-pointer appearance-none rounded-xl border-0 bg-surface-container-highest px-3.5 py-2 font-body-md text-body-md text-on-surface transition-transform active:scale-95"
                >
                  {table.shotLandsOnChat ? fa.report.shotDoorChat : fa.report.shotDoor}
                </button>
              </>
            ) : null}
          </div>

          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            {fa.report.prompt}
          </p>

          <div className="flex flex-col gap-1.5">
            {REPORT_REASONS.map((r) => {
              const active = reason === r;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(r)}
                  aria-pressed={active}
                  className={`flex cursor-pointer appearance-none items-center gap-3 rounded-2xl border px-3 py-2.5 text-right font-body-md text-body-md transition-colors ${
                    active
                      ? "border-secondary/70 bg-secondary/10 text-on-surface"
                      : "border-outline-variant/70 bg-surface-container-high text-on-surface-variant"
                  }`}
                >
                  <ReasonIcon reason={r} active={active} />
                  <span className="flex-1">
                    {fa.report.reasons[r]}
                    {/* Visible BEFORE the row is chosen, so the requirement is never a surprise. */}
                    {reportRequiresAttachment(r) ? <RequiredStar /> : null}
                  </span>
                  <span
                    aria-hidden="true"
                    className={`flex h-5 w-5 flex-none items-center justify-center rounded-full border text-caption ${
                      active
                        ? "border-secondary bg-secondary text-ink-on-gold"
                        : "border-outline-variant"
                    }`}
                  >
                    {active ? "✓" : ""}
                  </span>
                </button>
              );
            })}
          </div>

          <textarea
            rows={2}
            dir="rtl"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder={fa.report.detailsPlaceholder}
            aria-label={fa.report.detailsPlaceholder}
            maxLength={1000}
            className="resize-none appearance-none rounded-2xl border border-outline-variant/70 bg-surface-container-high px-3.5 py-2.5 font-body-md text-body-md text-on-surface outline-none placeholder:text-on-surface-variant focus:border-secondary"
          />

          {/* ── Evidence: required for توهین / محتوای نامناسب, welcome everywhere ── */}
          <div className="flex flex-col gap-2.5 rounded-2xl border border-outline-variant/70 bg-surface-container-high px-3.5 py-3">
            <div className="flex items-center justify-between gap-2">
              <p
                className={`m-0 flex items-center gap-2 font-body-md text-body-md font-bold ${
                  needsAttachment ? "text-error" : "text-on-surface"
                }`}
              >
                <span
                  className={`flex h-7 w-7 flex-none items-center justify-center rounded-full ${
                    needsAttachment
                      ? "bg-error/15 text-error"
                      : "bg-surface-container-highest text-on-surface-variant"
                  }`}
                >
                  <Glyph size={16}>{ATTACH_GLYPH}</Glyph>
                </span>
                {needsAttachment ? fa.report.attachTitleRequired : fa.report.attachTitleOptional}
                {needsAttachment ? <RequiredStar /> : null}
              </p>
              {/* ⚠️ ALWAYS RENDERED, including «۰ از ۳». It used to appear only once a file existed,
                  so the limit was invisible until the moment it bit. */}
              <span className="font-body-md text-caption text-on-surface-variant">
                {fa.report.attachCount(faNum(liveCount), faNum(MAX_REPORT_ATTACHMENTS))}
              </span>
            </div>
            <p className="m-0 font-body-md text-caption text-on-surface-variant">
              {needsAttachment ? fa.report.attachHint : fa.report.attachHintOptional}
            </p>

            <input
              ref={fileRef}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              aria-label={fa.report.attachPick}
              onChange={(e) => {
                // ⚠️ SNAPSHOT FIRST — THIS ORDER IS THE WHOLE BUG FIX. `e.target.files` is ONE
                // persistent FileList and `value = ""` CLEARS IT IN PLACE (measured in Chromium:
                // same object, length 2 → 0), so reading it after the reset yields nothing. That
                // shipped to prod and made the attachment silently do nothing for EVERY user on
                // every platform — no error, no bar, no chip. An array copy survives the reset.
                const files = Array.from(e.target.files ?? []);
                e.target.value = ""; // let the same file be re-picked after a failure
                if (files.length) pickFiles(files);
              }}
            />

            {items.length ? (
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {items.map((item) => (
                  <li
                    key={item.key}
                    data-testid={item.phase === "ready" ? "attach-chip" : `attach-${item.phase}`}
                    className="flex flex-col gap-1 rounded-xl bg-surface-container-highest px-2.5 py-1.5"
                  >
                    <div className="flex items-center gap-2">
                      <span aria-hidden="true">{item.phase === "failed" ? "⚠️" : "📎"}</span>
                      <span className="min-w-0 flex-1 truncate font-body-md text-caption text-on-surface">
                        {item.name}
                      </span>
                      {item.phase === "ready" ? (
                        <span className="font-body-md text-caption text-on-surface-variant">
                          {faBytes(item.bytes)}
                        </span>
                      ) : null}
                      {item.phase === "working" ? (
                        <span className="font-body-md text-caption text-on-surface-variant">
                          {faNum(Math.round(item.ratio * 100))}٪
                        </span>
                      ) : null}
                      {/* ⚠️ A REAL, VISIBLE CONTROL. A bare ✕ on transparent read as decoration —
                          and it is the ONLY way out of a failed pick, so it has to look pressable. */}
                      <button
                        type="button"
                        aria-label={fa.report.attachRemove}
                        onClick={() => {
                          setAttachNotice(null);
                          setItems((l) => l.filter((i) => i.key !== item.key));
                        }}
                        className="flex h-7 w-7 flex-none cursor-pointer appearance-none items-center justify-center rounded-full border border-outline-variant bg-surface-container text-body-md text-on-surface transition-colors active:bg-error-container active:text-on-error-container"
                      >
                        ✕
                      </button>
                    </div>
                    {item.phase === "working" ? (
                      <>
                        <div
                          role="progressbar"
                          aria-label={item.label}
                          aria-valuenow={Math.round(item.ratio * 100)}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          className="h-1.5 overflow-hidden rounded-full bg-surface-container"
                        >
                          <div
                            className="h-full rounded-full bg-secondary transition-[width] duration-200"
                            style={{ width: `${Math.max(3, Math.round(item.ratio * 100))}%` }}
                          />
                        </div>
                        <span className="font-body-md text-caption text-on-surface-variant">
                          {item.label}
                        </span>
                      </>
                    ) : null}
                    {item.phase === "failed" ? (
                      <span role="alert" className="font-body-md text-caption text-error">
                        {item.message}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}

            <button
              type="button"
              disabled={full}
              onClick={() => fileRef.current?.click()}
              className="cursor-pointer appearance-none rounded-xl border border-dashed border-outline-variant bg-surface-container px-3.5 py-2 font-body-md text-body-md text-on-surface transition-transform active:scale-95 disabled:opacity-50 disabled:active:scale-100"
            >
              {liveCount ? fa.report.attachAdd : fa.report.attachPick}
            </button>
            {attachNotice ? (
              <p role="alert" className="m-0 font-body-md text-caption text-error">
                {attachNotice}
              </p>
            ) : null}
            {full ? (
              <p className="m-0 font-body-md text-caption text-on-surface-variant">
                {fa.report.attachMaxReached}
              </p>
            ) : null}
          </div>

          {error ? (
            <p role="alert" className="m-0 font-body-md text-body-md text-error">
              {error}
            </p>
          ) : null}
          {reason && needsAttachment && ready.length === 0 && !working ? (
            <p className="m-0 font-body-md text-caption text-error">{fa.report.attachRequired}</p>
          ) : null}
          <button
            type="button"
            disabled={blocked}
            onClick={() => void submit()}
            /* ⚠️ ENABLED AND DISABLED ARE DIFFERENT SURFACES, NOT ONE SURFACE AT TWO OPACITIES.
               `bg-error` + `disabled:opacity-50` read as "a slightly dimmer red button" on this
               sheet, so a reporter with no reason picked — or with evidence still uploading — tapped
               something that looked live and got nothing. Disabled now drops the red entirely. */
            /* ⚠️ `bg-danger-deep`, NOT `bg-error`. `--c-error` is Material's error role for TEXT ON
               a dark surface — `#ffb4ab`, a pale salmon — so `bg-error` + `text-white` measured
               **1.70:1** in dark theme, far under the 4.5:1 floor, which is the «pale red» reported
               on device. Light theme was fine (6.46:1), which is why it looked wrong on only one.
               `danger-deep` is a deep red in BOTH themes: 7.77:1 dark, 9.35:1 light. */
            className="cursor-pointer appearance-none rounded-2xl border-0 bg-danger-deep py-3 font-body-md text-body-md font-bold text-white shadow-md transition-colors active:scale-95 disabled:cursor-not-allowed disabled:bg-surface-container-highest disabled:text-on-surface-variant disabled:shadow-none disabled:active:scale-100"
          >
            {busy ? fa.report.submitting : fa.report.submit}
          </button>
        </div>
      )}
    </Sheet>
  );
}
